import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Home, Languages, Share2, LogIn } from 'lucide-react';
import { Button } from './ui/button';
import BibleReaderChapterView from './BibleReaderChapterView';
import ThemeToggle from './ThemeToggle';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

/**
 * Standalone Bible Page - Full-screen chapter reading with YouVersion features
 * Shows entire chapter with all verses, click any verse for actions
 */
const BiblePage = () => {
  const navigate = useNavigate();
  const { language, toggleLanguage } = useLanguage();
  const { user } = useAuth();

  // Share the current chapter deep-link (native share on mobile, copy on desktop).
  const handleShare = async () => {
    const url = window.location.href;
    const title = document.title || 'Lumera Bible';
    try {
      if (navigator.share) {
        await navigator.share({ title, url });
        return;
      }
    } catch (e) {
      if (e && e.name === 'AbortError') return; // user dismissed the sheet
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success(language === 'es' ? 'Enlace copiado' : 'Link copied');
    } catch (e) {
      toast.error(language === 'es' ? 'No se pudo copiar' : 'Could not copy link');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Minimal floating header */}
      <div className="fixed top-4 left-4 right-4 z-50 flex items-center justify-between">
        <Button 
          variant="secondary" 
          size="sm" 
          onClick={() => navigate(user ? '/dashboard' : '/login')}
          className="shadow-lg"
          data-testid="bible-nav-primary"
        >
          {user ? <Home className="w-4 h-4 mr-2" /> : <LogIn className="w-4 h-4 mr-2" />}
          {user
            ? (language === 'es' ? 'Centro de Estudio' : 'Study Center')
            : (language === 'es' ? 'Iniciar sesión' : 'Sign in')}
        </Button>
        
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={handleShare}
            className="shadow-lg"
            title={language === 'es' ? 'Compartir capítulo' : 'Share chapter'}
            data-testid="bible-share-btn"
          >
            <Share2 className="w-4 h-4 sm:mr-1" />
            <span className="hidden sm:inline">{language === 'es' ? 'Compartir' : 'Share'}</span>
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={toggleLanguage}
            className="shadow-lg"
            data-testid="bible-language-toggle"
          >
            <Languages className="w-4 h-4 mr-1" />
            {language === 'es' ? 'ES' : 'EN'}
          </Button>
          <ThemeToggle />
        </div>
      </div>

      {/* Full-screen Chapter View with YouVersion features on each verse */}
      <main className="pt-16 px-4 pb-8 max-w-6xl mx-auto">
        <BibleReaderChapterView />
      </main>
    </div>
  );
};

export default BiblePage;
